import {
  Stage,
  StatusValue,
  WorkflowTemplate,
  WorkflowTemplates,
  StatusHistoryItem,
  StatusEquivalents,
} from "./types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { formatDuration } from "src/utils/duration";

dayjs.extend(duration);

function isStatusEquivalent(
  status: string | undefined,
  stageName: StatusValue
): boolean {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return StatusEquivalents[stageName].includes(normalizedStatus);
}

export function generateStagesFromTemplate(
  template: WorkflowTemplate | undefined,
  currentStatus: string | undefined,
  statusHistory: StatusHistoryItem[] = []
): Stage[] {
  const templateStages = template
    ? WorkflowTemplates[template]
    : WorkflowTemplates.DEFAULT;
  const sortedHistory = [...statusHistory].sort(
    (a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
  );
  return templateStages.map((stageName: StatusValue, stageIndex) => {
    // Use equivalent status matching
    const stageHistory = sortedHistory.filter((h) =>
      isStatusEquivalent(h.status, stageName)
    );
    const isCurrentStage = isStatusEquivalent(currentStatus, stageName);
    const currentStageIndex = templateStages.findIndex((s) =>
      isStatusEquivalent(currentStatus, s)
    );
    const isLastStage = stageIndex === templateStages.length - 1;

    let status: Stage["status"] = "pending";
    if (isCurrentStage) {
      status = "current";
    } else if (
      stageIndex < currentStageIndex ||
      (stageHistory.length > 0 && !isCurrentStage)
    ) {
      status = "completed";
    }

    let duration: number | undefined;
    let timestamp: string | undefined;

    // If we have history for this stage
    if (stageHistory.length > 0) {
      // Always use the earliest timestamp for the stage
      const firstStageEvent = stageHistory[0];
      timestamp = firstStageEvent.timestamp;

      // Calculate duration based on stage position
      if (isLastStage) {
        // For last stage, duration is from first event of the workflow to last event
        duration = dayjs(stageHistory.at(-1)?.timestamp).diff(
          dayjs(sortedHistory[0].timestamp)
        );
      } else if (stageIndex === 0) {
        // For first stage, duration is until the next different status
        const nextStageEvent = sortedHistory.find(
          (h) => !isStatusEquivalent(h.status, stageName)
        );
        duration = nextStageEvent
          ? dayjs(nextStageEvent.timestamp).diff(
              dayjs(firstStageEvent.timestamp)
            )
          : isCurrentStage
          ? dayjs().diff(dayjs(firstStageEvent.timestamp))
          : undefined;
      } else {
        // For middle stages
        const nextStageEvent = sortedHistory.find(
          (h) =>
            dayjs(h.timestamp).isAfter(dayjs(firstStageEvent.timestamp)) &&
            !isStatusEquivalent(h.status, stageName)
        );

        if (isCurrentStage) {
          duration = dayjs().diff(dayjs(firstStageEvent.timestamp));
        } else {
          duration = nextStageEvent
            ? dayjs(nextStageEvent.timestamp).diff(
                dayjs(firstStageEvent.timestamp)
              )
            : dayjs(stageHistory.at(-1)?.timestamp).diff(
                dayjs(firstStageEvent.timestamp)
              );
        }
      }
    }

    return {
      name: stageName,
      status,
      timestamp,
      duration,
    };
  });
}

//transform the events into common format for easy usage
export function normalizeEvents(events: any[]): any[] {
  return events.map((event: any) => {
    // If event doesn't have attributes, return as is
    if (!event?.attributes) {
      return event;
    }

    // Create a new normalized event object
    const normalizedEvent: any = {
      ...event,
      // Remove the original attributes object after normalization
      attributes: undefined,
    };

    // Handle both Event.* prefixed attributes and nested attributes
    Object.entries(event.attributes).forEach(([key, value]) => {
      if (key.startsWith("Event.")) {
        // Handle Event.* prefixed attributes
        const normalizedKey = key.replace("Event.", "").toLowerCase();
        normalizedEvent[normalizedKey] = value;
      } else {
        // Handle regular attributes (like 'action', 'status', etc)
        const normalizedKey = key.toLowerCase();
        normalizedEvent[normalizedKey] = value;
      }
    });

    // Special handling for nested action if it exists
    if (event.attributes.action !== undefined) {
      normalizedEvent.action = event.attributes.action;
    }

    return normalizedEvent;
  });
}

// Map events to workflow states based on workflow template and StatusEquivalents
export function mapEventToWorkflowStatus(
  event: any,
  workflow: WorkflowTemplate | undefined
): string | null {
  if (!workflow) return event?.newstatus || null;

  const action = event?.action?.toLowerCase();
  const newStatus = event?.newstatus?.toLowerCase();
  const templateStages = WorkflowTemplates[workflow];

  // First try to map based on the new status
  if (newStatus) {
    // Find the matching stage from the workflow template
    const matchingStage = templateStages.find((stage) =>
      StatusEquivalents[stage].includes(newStatus)
    );
    if (matchingStage) return matchingStage;
  }

  // Then try to map based on the action
  if (action) {
    switch (workflow) {
      case "PR_WORKFLOW": {
        // Special handling for PR-specific actions
        if (action === "created") return event.draft ? "Draft" : "Open";
        if (action === "ready_for_review" || action === "review_requested")
          return "In Review";
        if (action === "converted_to_draft") return "Draft";
        if (action === "changes_requested") return "Changes Requested";
        if (action === "approved") return "Approved";
        if (action === "merged") return "Merged";
        break;
      }
      case "DEPLOYMENT": {
        // Handle deployment-specific actions
        if (action === "created") return "Pending";
        if (action === "started") return "In Progress";
        if (action === "completed") return "Completed";
        break;
      }
      default: {
        // For other workflows, try to map the action to a status
        if (action === "created") {
          // Use the first stage of the workflow as the initial state
          return templateStages[0];
        }
        break;
      }
    }
  }

  // If no mapping found, try to find an equivalent status from the workflow
  const normalizedStatus = (event?.newstatus || "").toLowerCase();
  for (const stage of templateStages) {
    if (StatusEquivalents[stage].includes(normalizedStatus)) {
      return stage;
    }
  }

  return null;
}

export function generateStatusHistoryFromEvents(
  events: any[],
  entity?: any,
  workflow?: WorkflowTemplate
): StatusHistoryItem[] {
  let normalizedEvents = normalizeEvents(events);
  let statusHistory = normalizedEvents
    .filter(
      (event: any) =>
        event?.action === "created" ||
        (event?.newstatus !== undefined &&
          event?.newstatus !== "__VZBL::null__")
    )
    .map((event: any): StatusHistoryItem => {
      const status =
        mapEventToWorkflowStatus(event, workflow) ||
        event?.newstatus ||
        (event?.action === "created" ? "Created" : null);

      return {
        status,
        timestamp: event?.occurredat,
      };
    })
    .filter((item: StatusHistoryItem) => item.status !== null);

  // Add creation event if missing and we have entity data
  if (entity?.properties?.createdAt) {
    const hasCreationEvent = statusHistory.some(
      (item) =>
        item.status.toLowerCase() === "created" ||
        item.timestamp === entity.properties.createdAt
    );

    if (!hasCreationEvent && workflow) {
      // Use the first stage from the workflow template as the initial state
      const templateStages = WorkflowTemplates[workflow];
      const initialStatus =
        workflow === "PR_WORKFLOW"
          ? entity.properties.draft
            ? "Draft"
            : templateStages[0]
          : templateStages[0];

      statusHistory.unshift({
        status: initialStatus,
        timestamp: entity.properties.createdAt,
      });
    }
  }

  // Sort by timestamp to ensure correct order
  statusHistory.sort(
    (a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
  );

  return statusHistory;
}

export function calculateDuration(startTime: string, endTime?: string): number {
  return endTime
    ? dayjs(endTime).diff(dayjs(startTime))
    : dayjs().diff(dayjs(startTime));
}

export { formatDuration };
